import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Image,
  Input,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { AppBar, AppIcon, APP } from "../../components/mobileUi";
import {
  DesktopPage,
  DesktopPanel,
  DesktopSearch,
  DesktopTable,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { getSalesOpeningEventListApi } from "../../api/finance.salesOpening.api";
import { getProjectListApi } from "../../api/finance.project.api";
import { resolveImageUrl } from "../../utils/format";
import { formatArea, formatPrice, formatTotalApartment } from "../../utils/financeFormat";
import { EMP_BASE_PATH, EMP_API_BASE_URL } from "../../config";

const BORDER = "#E1E1E1";
const TEXT = "#08142D";
const MUTED = "#94A3B8";
const LIMIT = 10;

const SORT_OPTIONS = [
  { key: "NEWEST", label: "Từ mới đến cũ", sortOrder: 1 },
  { key: "OLDEST", label: "Từ cũ đến mới", sortOrder: 2 },
];

const fmtD = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

const summaryOf = (links = []) => {
  const list = Array.isArray(links) ? links : [];
  if (!list.length) return { total: "0 căn", value: "0 tỷ" };
  const values = list.map((x) => Number(x?.apartment?.totalValue) || 0).filter((v) => v > 0);
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 0;
  return {
    total: formatTotalApartment(list.length),
    value: min && max ? `${formatPrice(min)} - ${formatPrice(max)}` : "0",
  };
};

const EventCard = ({ item, onClick }) => {
  const img = item?.project?.images?.[0]?.path
    ? resolveImageUrl(item.project.images[0].path, EMP_API_BASE_URL)
    : appImages.image_project_example;
  const s = summaryOf(item?.apartmentSaleOpeningEventLink);
  return (
    <Box as="button" type="button" onClick={onClick} textAlign="left" w="100%" bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px" overflow="hidden">
      <Image src={img} alt="" w="100%" h="180px" objectFit="cover" />
      <Box p="16px">
        <Text fontSize="16px" fontWeight={600} color={TEXT} noOfLines={2}>{item?.name || "-"}</Text>
        <Flex align="center" gap="8px" mt="8px" color={APP.primary200} fontWeight={600} fontSize="15px">
          <Text>{s.total}</Text>
          <Box w="4px" h="4px" borderRadius="full" bg={APP.primary200} />
          <Text noOfLines={1}>{s.value}</Text>
        </Flex>
        <Text fontSize="14px" color={MUTED} mt="8px" noOfLines={1}>
          {fmtD(item?.startTime)} - {fmtD(item?.endTime)}
        </Text>
      </Box>
    </Box>
  );
};

const SalesOpeningEventList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [filterOpen, setFilterOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [project, setProject] = useState({ _id: "all", name: "Tất cả" });
  const [sortKey, setSortKey] = useState("NEWEST");

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);

  // Sheet dự án (có tìm kiếm)
  const sheet = useDisclosure();
  const [sheetType, setSheetType] = useState(null); // PROJECT | SORT
  const [projects, setProjects] = useState([]);
  const [projSearch, setProjSearch] = useState("");
  const reqId = useRef(0);
  const searchTimer = useRef(null);
  const projTimer = useRef(null);

  const sortOption = SORT_OPTIONS.find((x) => x.key === sortKey) || SORT_OPTIONS[0];

  const buildParams = useCallback(
    (p) => ({
      page: p,
      limit: LIMIT,
      projectId: project?._id && project._id !== "all" ? project._id : undefined,
      type: sortOption.sortOrder,
      search: search || undefined,
    }),
    [project, sortOption, search]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getSalesOpeningEventListApi(buildParams(p));
        if (rid !== reqId.current) return;
        const list = data?.items || data || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchPage(1, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project._id, sortKey, search]);

  // Tải danh sách dự án khi mở sheet / đổi từ khoá
  const loadProjects = useCallback((kw) => {
    getProjectListApi({ page: 1, limit: 30, search: kw || undefined })
      .then((d) => setProjects(d?.items || d || []))
      .catch(() => setProjects([]));
  }, []);

  const openSheet = (type) => {
    setSheetType(type);
    if (type === "PROJECT") {
      setProjSearch("");
      loadProjects("");
    }
    sheet.onOpen();
  };

  const onSearchChange = (e) => {
    const v = e.target.value;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(v.trim()), 300);
  };
  const onProjSearch = (e) => {
    const v = e.target.value;
    setProjSearch(v);
    if (projTimer.current) clearTimeout(projTimer.current);
    projTimer.current = setTimeout(() => loadProjects(v.trim()), 300);
  };

  const FieldBox = ({ label, value, onClick }) => (
    <Box flex="1" minW="0">
      <Text fontSize="13px" color={TEXT} mb="6px">{label}</Text>
      <Flex as="button" type="button" onClick={onClick} align="center" justify="space-between" bg="#F7F9FC" borderRadius="8px" h="40px" px="10px" w="100%">
        <Text fontSize="14px" color={TEXT} noOfLines={1}>{value}</Text>
        <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
      </Flex>
    </Box>
  );

  const projectItems = useMemo(() => [{ _id: "all", name: "Tất cả" }, ...projects], [projects]);

  const bottomSheet = (
    <>
      <Drawer isOpen={sheet.isOpen} onClose={sheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="8px">{sheetType === "PROJECT" ? "Dự án" : "Sắp xếp"}</Text>
            {sheetType === "PROJECT" && (
              <Flex h="40px" align="center" px="10px" bg="#F7F9FC" borderRadius="8px" mb="8px">
                <AppIcon src={appImages.icon_search} size={18} color="#94A3B8" />
                <Input variant="unstyled" ml="8px" fontSize="14px" placeholder="Tìm dự án..." value={projSearch} onChange={onProjSearch} />
              </Flex>
            )}
            <Box maxH="50vh" overflowY="auto">
              {(sheetType === "PROJECT" ? projectItems : SORT_OPTIONS).map((it, i, arr) => {
                const raw = sheetType === "PROJECT" ? it : it.key;
                const label = sheetType === "PROJECT" ? it.name : it.label;
                const active = sheetType === "PROJECT" ? raw?._id === project?._id : raw === sortKey;
                const last = i === arr.length - 1;
                return (
                  <Flex
                    key={sheetType === "PROJECT" ? it._id : it.key}
                    as="button"
                    w="100%"
                    align="center"
                    justify="space-between"
                    py="12px"
                    borderBottom={last ? "none" : "1px solid #EEF1F6"}
                    onClick={() => {
                      if (sheetType === "PROJECT") setProject(it);
                      else setSortKey(it.key);
                      sheet.onClose();
                    }}
                  >
                    <Text fontSize="15px" color={active ? APP.primary200 : TEXT} fontWeight={active ? 600 : 400} textAlign="left">{label}</Text>
                    {active && <img src={appImages.icon_check} alt="" width={18} height={18} style={{ objectFit: "contain" }} />}
                  </Flex>
                );
              })}
            </Box>
            <Flex as="button" onClick={sheet.onClose} mt="12px" w="100%" h="46px" align="center" justify="center" borderRadius="10px" bg={APP.primary200} color="white" fontWeight={700}>Đóng</Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((it) => {
      const s2 = summaryOf(it?.apartmentSaleOpeningEventLink);
      return {
        id: it._id,
        img: it?.project?.images?.[0]?.path
          ? resolveImageUrl(it.project.images[0].path, EMP_API_BASE_URL)
          : appImages.image_project_example,
        name: it?.name || "-",
        project: it?.project?.name || "-",
        total: s2.total,
        value: s2.value,
        time: `${fmtD(it?.startTime)} - ${fmtD(it?.endTime)}`,
      };
    });

    return (
      <DesktopPage title="Đợt mở bán">
        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            <Box w="320px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Tìm kiếm
              </Text>
              <DesktopSearch
                placeholder="Nhập tên đợt mở bán ..."
                onChange={onSearchChange}
                maxW="320px"
              />
            </Box>
            <Box w="240px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Dự án
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                bg="#F7F9FC"
                borderRadius="8px"
                fontSize="14px"
                onClick={() => openSheet("PROJECT")}
              >
                {project?.name || "Tất cả"}
              </Box>
            </Box>
            <Box w="200px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Sắp xếp
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                bg="#F7F9FC"
                borderRadius="8px"
                fontSize="14px"
                onClick={() => openSheet("SORT")}
              >
                {sortOption.label}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            emptyText="Không có đợt mở bán"
            onRowClick={(row) => navigate(`${EMP_BASE_PATH}/tai-chinh/dot-mo-ban/${row.id}`)}
            rows={rows}
            headers={[
              {
                key: "img",
                label: "Ảnh",
                width: "10%",
                render: (row) => (
                  <Image
                    src={row.img}
                    alt=""
                    w="72px"
                    h="48px"
                    objectFit="cover"
                    borderRadius="8px"
                    mx="auto"
                  />
                ),
              },
              { key: "name", label: "Đợt mở bán", align: "left", width: "26%" },
              { key: "project", label: "Dự án", align: "left", width: "20%" },
              { key: "total", label: "Số căn", width: "14%" },
              { key: "value", label: "Giá trị", width: "14%" },
              { key: "time", label: "Thời gian", width: "16%" },
            ]}
          />

          {hasNext && (
            <Flex
              as="button"
              onClick={() => fetchPage(page + 1, "append")}
              justify="center"
              py="16px"
              w="100%"
              color={APP.primary200}
              fontWeight={600}
              fontSize="14px"
            >
              {loading ? <Spinner size="sm" /> : "Xem thêm"}
            </Flex>
          )}
        </DesktopPanel>

        {bottomSheet}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Đợt mở bán" onBack={() => navigate(EMP_BASE_PATH)} />

      <Box px="16px" pt="12px">
        <Flex h="44px" align="center" px="12px" bg="white" border="1px solid" borderColor={BORDER} borderTopRadius="12px" borderBottom={filterOpen ? "0" : "1px solid"} borderBottomColor={BORDER} borderBottomRadius={filterOpen ? "0" : "12px"}>
          <AppIcon src={appImages.icon_search} size={20} color="#94A3B8" />
          <Input variant="unstyled" ml="8px" fontSize="14px" fontWeight={500} placeholder="Nhập tên đợt mở bán ..." onChange={onSearchChange} />
        </Flex>

        <Flex as="button" w="100%" h="44px" px="12px" align="center" justify="space-between" bg="white" border="1px solid" borderColor={BORDER} borderTop="0" borderBottomRadius={filterOpen ? "0" : "12px"} onClick={() => setFilterOpen((p) => !p)}>
          <Flex align="center" gap="8px">
            <img src={appImages.icon_filter_detail} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
            <Text fontSize="14px" fontWeight={600} color={TEXT}>Bộ lọc chi tiết</Text>
          </Flex>
          <img src={filterOpen ? appImages.icon_chevron_up : appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
        </Flex>
        <Collapse in={filterOpen} animateOpacity>
          <Box bg="white" border="1px solid" borderColor={BORDER} borderTop="0" borderBottomRadius="12px" p="12px">
            <FieldBox label="Dự án" value={project?.name || "Tất cả"} onClick={() => openSheet("PROJECT")} />
          </Box>
        </Collapse>

        <Flex mt="16px" mb="14px" align="center" justify="space-between">
          <Text fontSize="14px" fontWeight={600} color={TEXT}>Danh sách đợt mở bán</Text>
          <Flex as="button" align="center" gap="6px" onClick={() => openSheet("SORT")}>
            <Text fontSize="14px" fontWeight={600} color={TEXT}>{sortOption.label}</Text>
            <img src={appImages.ion_sort_desc} alt="" width={16} height={16} style={{ objectFit: "contain" }} />
          </Flex>
        </Flex>

        {loading && !items.length ? (
          <Flex justify="center" py="40px"><Spinner color={APP.primary200} /></Flex>
        ) : items.length === 0 ? (
          <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">Không có đợt mở bán</Text>
        ) : (
          <Flex direction="column" gap="10px">
            {items.map((it) => (
              <EventCard key={it._id} item={it} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/dot-mo-ban/${it._id}`)} />
            ))}
            {hasNext && (
              <Flex as="button" onClick={() => fetchPage(page + 1, "append")} justify="center" py="12px" color={APP.primary200} fontWeight={600} fontSize="14px">
                {loading ? <Spinner size="sm" /> : "Xem thêm"}
              </Flex>
            )}
          </Flex>
        )}
      </Box>

      {bottomSheet}
    </Box>
  );
};

export default SalesOpeningEventList;
